import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { generateDateTicks } from "../../../../services/dataTicks";

const FuelConsumptionMCR = ({ data }) => {

  console.log("fuelvsmcr:", data);
  // Use the provided data from API response
  // const chartData = data.map((item) => ({
  //   date: item.date,
  //   mcr: item.mcr,
  //   consumption: item.consumption,
  // }));
  const normalizedData = Array.isArray(data)
  ? data
  : [data];

  const chartData = normalizedData.map(item => ({
    date: item.date ?? null,
    mcr: item.mcr,
    consumption: item.consumption,
  }));
  const dates = chartData ? chartData.map((item) => item.date) : [];

  let tickPositions = generateDateTicks(dates);

  const consumptionData = chartData.map((item) => [
    new Date(item.date).getTime(),
    item.consumption,
  ]);

  const mcrData = chartData.map((item) => [
    new Date(item.date).getTime(), // Convert to timestamp
    item.mcr,
  ]);

  // Calculate combined efficiency trend line
  const calculateCombinedTrendLine = () => {
    if (chartData.length < 2) return [];

    // Calculate efficiency ratio (consumption per unit MCR) over time
    // Handle zero MCR values by using a small number to avoid division by zero
    const efficiencyData = chartData.map((item, index) => {
      const effectiveMcr = item.mcr === 0 ? 0.001 : item.mcr; // Use small value instead of zero to avoid infinity
      const efficiency = item.consumption / effectiveMcr;
      return [
        new Date(item.date).getTime(), // timestamp
        efficiency
      ];
    });

    if (efficiencyData.length < 2) return [];

    // Linear regression on efficiency over time
    const n = efficiencyData.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

    efficiencyData.forEach((point, index) => {
      const x = index; // time index
      const y = point[1]; // efficiency value
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Create trend line for efficiency
    return efficiencyData.map((point, index) => [
      point[0], // original timestamp
      slope * index + intercept // trend efficiency value
    ]);
  };

  const combinedTrendData = calculateCombinedTrendLine();

  const chartOptions = {
    chart: {
      type: "scatter",
      height: "500px",
      backgroundColor: "transparent",
      spacing: [20, 15, 15, 15],
    },
    title: {
      text: null,
    },
    xAxis: {
      type: "datetime",
      tickPositions: tickPositions,
      title: {
        text: null,
      },
      labels: {
        format: "{value:%b %e}",
        style: {
          color: "#666666",
        },
      },
      gridLineWidth: 1,
      gridLineColor: "#f0f0f0",
    },
    yAxis: [
      {
        title: {
          text: "% MCR",
          style: {
            color: "#7d3c98",
            fontSize: "12px",
          },
        },
        min: 0,
        max: 150,
        tickInterval: 20,
        gridLineWidth: 1,
        gridLineColor: "#f0f0f0",
        labels: {
          style: {
            color: "#666666",
          },
        },
        opposite: false, // MCR on left side
      },
      {
        title: {
          text: "Avg ME Consumption (MT/day)",
          style: {
            color: "#1a5276",
            fontSize: "12px",
          },
        },
        min: 0,
        gridLineWidth: 0,
        labels: {
          style: {
            color: "#666666",
          },
        },
        opposite: true, // Fuel consumption on right side
      },
    ],
    legend: {
      align: "right",
      verticalAlign: "top",
      itemStyle: {
        color: "#666666",
        fontWeight: "normal",
      },
      itemHoverStyle: {
        color: "#333333",
      },
      symbolRadius: 0,
    },
    tooltip: {
      shared: true,
      crosshairs: true,
      backgroundColor: "#ffffff",
      borderColor: "#e5e7eb",
      borderRadius: 6,
      shadow: true,
      useHTML: true,
      headerFormat:
        '<div style="font-weight:600;font-size:12px;margin-bottom:5px">{point.x:%b %e, %Y}</div>',
      pointFormat:
        '<div style="display:flex;justify-content:space-between"><span style="color:{series.color}">{series.name}:</span><span style="font-weight:700;margin-left:10px">{point.y}</span></div>',
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 5,
          symbol: "circle",
          lineWidth: 2,
          lineColor: "#ffffff",
          states: {
            hover: {
              enabled: true,
              lineColor: "#333333",
              radius: 6,
            },
          },
        },
        states: {
          hover: {
            halo: {
              size: 8,
            },
          },
        },
      },
      line: {
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            halo: {
              size: 0,
            },
          },
        },
      },
    },
    series: [
      {
        name: "Actual Consumption",
        type: "scatter",
        data: consumptionData,
        color: "#1a5276",
        yAxis: 1,
        marker: {
          fillColor: "#1a5276",
          lineWidth: 2,
          lineColor: "#ffffff",
          radius: 5,
        },
        tooltip: {
          valueSuffix: " MT/day",
        },
      },
      {
        name: "% MCR",
        type: "scatter",
        data: mcrData,
        color: "#7d3c98",
        yAxis: 0,
        marker: {
          fillColor: "#7d3c98",
          lineWidth: 2,
          lineColor: "#ffffff",
          radius: 5,
        },
        tooltip: {
          valueSuffix: " %",
        },
      },
      {
        name: "Efficiency Trend",
        type: "line",
        data: combinedTrendData,
        color: "#e74c3c",
        yAxis: 1,
        lineWidth: 2,
        dashStyle: "Dash",
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 3,
          },
        },
        tooltip: {
          valueSuffix: " MT/day/% MCR",
          pointFormat: '<div style="display:flex;justify-content:space-between"><span style="color:{series.color}">{series.name}:</span><span style="font-weight:700;margin-left:10px">{point.y:.3f}</span></div>'
        },
      }
    ],
    credits: {
      enabled: false,
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            legend: {
              align: "center",
              verticalAlign: "bottom",
              layout: "horizontal",
            },
            xAxis: {
              labels: {
                format: "{value:%b %e}",
              },
            },
          },
        },
      ],
    },
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="flex justify-between items-center">
        <h1>Avg ME Consumption & % MCR over Time</h1>
      </div>

      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        containerProps={{
          className: "w-full",
          style: { height: "500px" },
        }}
      />
    </div>
  );
};

export default FuelConsumptionMCR;